import { isBuilderDomain } from 'utils/getMatomoSiteId';

export const MATCH_REGEXP = 'matchRegexp:';
export const RULE_MAX_NUMBER = 'maxNumber:';
export const RULE_MIN_NUMBER = 'minNumber:';

export const VALIDATORS_MAP = {
  required: {
    rule: 'required',
    message: 'This field is required',
  },
  isEmail: {
    rule: 'isEmail',
    message: 'Please use correct email format',
  },
  minPasswordLength: (num: number) => ({
    rule: `minStringLength:${num}`,
    message: `The password is too short (minimum is ${num} characters)`,
  }),
  maxStringLength: (num: number) => ({
    rule: `maxStringLength:${num}`,
    message: `The name is too long (maximum is ${num} characters)`,
  }),
  maxNumber: (max: number) => ({
    rule: `${RULE_MAX_NUMBER}${max}`,
    message: `Maximum value is ${max}`,
  }),
  minNumber: (min: number) => ({
    rule: `${RULE_MIN_NUMBER}${min}`,
    message: `Minimum value is ${min}`,
  }),
  isPositive: {
    rule: 'isPositive',
    message: 'Minimum value is 0',
  },
  isValidDomain: {
    rule: 'matchRegexp:^\\S+\\.\\S+$',
    message: 'Please use correct domain name',
  },
  isValidCustomDomain: {
    rule: `${MATCH_REGEXP}^[0-9a-z-_.]+$`,
    message: 'Only lowercase letters, numbers, underscores, dashes, dots are allowed',
  },
  lowCaseWithoutSpecialSymbols: {
    rule: `${MATCH_REGEXP}^[0-9a-z -]+$`,
    message: 'Low case without special symbols',
  },
  lettersNumbersUnderscoresDashDot: {
    rule: 'matchRegexp:^[0-9a-zA-Z-_.]+$',
    message: 'Only letters, numbers, underscores, dashes, dots are allowed',
  },
  lettersNumbersUnderscores: {
    rule: 'matchRegexp:^[0-9a-zA-Z_]+$',
    message: 'Only letters, numbers, underscores are allowed',
  },
  lettersNumbersSpacesUnderscoresDash: {
    rule: `${MATCH_REGEXP}^[0-9a-zA-Z-_ ]+$`,
    message: 'Only letters, numbers, underscores, dashes, spaces are allowed',
  },
  onlyLettersNumbers: {
    rule: `${MATCH_REGEXP}^[0-9A-z-_]+$`,
    message: 'Only letters, numbers, underscores, dashes are allowed',
  },
  lowercaseLettersNumbersUnderscoreDashes: {
    rule: `${MATCH_REGEXP}^[0-9a-z-_]*$`,
    message: 'Only lowercase letters, numbers, underscore, dashes are allowed',
  },
  withoutSpecialSymbols: {
    rule: `${MATCH_REGEXP}^[0-9a-zA-Z -]+$`,
    message: 'No special characters allowed',
  },
  numbersOnly: {
    rule: `${MATCH_REGEXP}^[0-9]+$`,
    message: 'Please use digits only',
  },
  antiXSSRegexp: {
    rule: 'matchCustomRegexp',
    message: 'Without special symbols',
  },
  youtubeLink: {
    // eslint-disable-next-line max-len
    rule: `${MATCH_REGEXP}(?:youtube\\.com\\/\\S*(?:(?:\\/e(?:mbed))?\\/|watch\\?(?:\\S*?&?v\\=))|youtu\\.be\\/)([a-zA-Z0-9_-]{6,11})`,
    message: 'Only valid YouTube link',
  },
  lettersNumbersSpecialSymbols: {
    rule: `${MATCH_REGEXP}^[0-9a-zA-Z ™:;'",.!@#$%^&?*()-_+=|®~/|₴<>]+$`,
    message:
      'Only upper, lower case letters, numbers,™: ;\'",. !@#$%^&?*()-_+=|®~/|₴[]<>white space (several white spaces) symbols are allowed',
  },
  spaceName: {
    rule: `${MATCH_REGEXP}^[0-9a-z-_]+$`,
    message: `${
      isBuilderDomain() ? 'World' : 'Space'
    } Name must only consist of lowercase letters, numbers, underscores and dashes`,
  },
  fontFeature: {
    rule: `${MATCH_REGEXP}^(?:(\\w{4})|(?:(?:,\\s)?(['"][\\w]{4}['"])(?:\\s(?:[01]|on|off))?)+)$`,
    message: 'Only lowercase letters, numbers, quotation marks and commas are allowed.',
  },
  https: (fieldName: string) => ({
    rule: `${MATCH_REGEXP}^(https:\\/\\/)`,
    message: `${fieldName} must be HTTPS.`,
  }),
  notEmpty: (message: string) => ({
    rule: 'required',
    message: `Please enter ${message}`,
  }),
  requiredNoMessage: {
    rule: 'required',
    message: '',
  },
};

export const matchCustomRegexpRule =
  /<[^\w<>]*(?:[^<>"'\s]*:)?[^\w<>]*(?:\W*s\W*c\W*r\W*i\W*p\W*t|\W*f\W*o\W*r\W*m|\W*s\W*t\W*y\W*l\W*e|\W*s\W*v\W*g|\W*m\W*a\W*r\W*q\W*u\W*e\W*e|(?:\W*l\W*i\W*n\W*k|\W*o\W*b\W*j\W*e\W*c\W*t|\W*e\W*m\W*b\W*e\W*d|\W*a\W*p\W*p\W*l\W*e\W*t|\W*p\W*a\W*r\W*a\W*m|\W*i?\W*f\W*r\W*a\W*m\W*e|\W*b\W*a\W*s\W*e|\W*b\W*o\W*d\W*y|\W*m\W*e\W*t\W*a|\W*i\W*m\W*a?\W*g\W*e?|\W*v\W*i\W*d\W*e\W*o|\W*a\W*u\W*d\W*i\W*o|\W*b\W*i\W*n\W*d\W*i\W*n\W*g\W*s|\W*s\W*e\W*t|\W*i\W*s\W*i\W*n\W*d\W*e\W*x|\W*a\W*n\W*i\W*m\W*a\W*t\W*e)[^>\w])|(?:<\w[\s\S]*[\s\0]|['"])(?:formaction|style|background|src|lowsrc|ping|on(?:d(?:e(?:vice(?:(?:orienta|mo)tion|proximity|found|light)|livery(?:success|error)|activate)|r(?:ag(?:e(?:n(?:ter|d)|xit)|(?:gestur|leav)e|start|drop|over)?|op)|i(?:s(?:c(?:hargingtimechange|onnect(?:ing|ed))|abled)|aling)|ata(?:setc(?:omplete|hanged)|(?:availabl|chang)e|error)|urationchange|ownloading|blclick)|Moz(?:M(?:agnifyGesture(?:Update|Start)?|ouse(?:PixelScroll|Hittest))|S(?:wipeGesture(?:Update|Start|End)?|crolledAreaChanged)|(?:(?:Press)?TapGestur|BeforeResiz)e|EdgeUI(?:C(?:omplet|ancel)|Start)ed|RotateGesture(?:Update|Start)?|A(?:udioAvailable|fterPaint))|c(?:o(?:m(?:p(?:osition(?:update|start|end)|lete)|mand(?:update)?)|n(?:t(?:rolselect|extmenu)|nect(?:ing|ed))|py)|a(?:(?:llschang|ch)ed|nplay(?:through)?|rdstatechange)|h(?:(?:arging(?:time)?ch)?ange|ecking)|(?:fstate|ell)change|u(?:echange|t)|l(?:ick|ose))|m(?:o(?:z(?:pointerlock(?:change|error)|(?:orientation|time)change|fullscreen(?:change|error)|network(?:down|up)load)|use(?:(?:lea|mo)ve|o(?:ver|ut)|enter|wheel|down|up)|ve(?:start|end)?)|essage|ark)|s(?:t(?:a(?:t(?:uschanged|echange)|lled|rt)|k(?:sessione|comma)nd|op)|e(?:ek(?:complete|ing|ed)|(?:lec(?:tstar)?)?t|n(?:ding|t))|u(?:ccess|spend|bmit)|peech(?:start|end)|ound(?:start|end)|croll|how)|b(?:e(?:for(?:e(?:(?:scriptexecu|activa)te|u(?:nload|pdate)|p(?:aste|rint)|c(?:opy|ut)|editfocus)|deactivate)|gin(?:Event)?)|oun(?:dary|ce)|l(?:ocked|ur)|roadcast|usy)|a(?:n(?:imation(?:iteration|start|end)|tennastatechange)|fter(?:(?:scriptexecu|upda)te|print)|udio(?:process|start|end)|d(?:apteradded|dtrack)|ctivate|lerting|bort)|DOM(?:Node(?:Inserted(?:IntoDocument)?|Removed(?:FromDocument)?)|(?:CharacterData|Subtree)Modified|A(?:ttrModified|ctivate)|Focus(?:Out|In)|MouseScroll)|r(?:e(?:s(?:u(?:m(?:ing|e)|lt)|ize|et)|adystatechange|pea(?:tEven)?t|movetrack|trieving|ceived)|ow(?:s(?:inserted|delete)|e(?:nter|xit))|atechange)|p(?:op(?:up(?:hid(?:den|ing)|show(?:ing|n))|state)|a(?:ge(?:hide|show)|(?:st|us)e|int)|ro(?:pertychange|gress)|lay(?:ing)?)|t(?:ouch(?:(?:lea|mo)ve|en(?:ter|d)|cancel|start)|ime(?:update|out)|ransitionend|ext)|u(?:s(?:erproximity|sdreceived)|p(?:gradeneeded|dateready)|n(?:derflow|load))|f(?:o(?:rm(?:change|input)|cus(?:out|in)?)|i(?:lterchange|nish)|ailed)|l(?:o(?:ad(?:e(?:d(?:meta)?data|nd)|start)?|secapture)|evelchange|y)|g(?:amepad(?:(?:dis)?connected|button(?:down|up)|axismove)|et)|e(?:n(?:d(?:Event|ed)?|abled|ter)|rror(?:update)?|mptied|xit)|i(?:cc(?:cardlockerror|infochange)|n(?:coming|valid|put))|o(?:(?:(?:ff|n)lin|bsolet)e|verflow(?:changed)?|pen)|SVG(?:(?:Unl|L)oad|Resize|Scroll|Abort|Error|Zoom)|h(?:e(?:adphoneschange|l[dp])|ashchange|olding)|v(?:o(?:lum|ic)e|ersion)change|w(?:a(?:it|rn)ing|heel)|key(?:press|down|up)|(?:AppComman|Loa)d|no(?:update|match)|Request|zoom))[\s\0]*=/gi;
